

function solveUnweightedLeastSquaresDeg2(x: number[], y: number[], count: number) {
    if (count <= 0) {
        return 0;
    }
    let sxi = 0;
    let sxiyi = 0;
    let syi = 0;
    let sxi2 = 0;
    let sxi3 = 0;
    let sxi2yi = 0;
    let sxi4 = 0;

    for (let i = 0; i < count; i++) {
        const xi = x[i];
        const yi = y[i];
        const xi2 = xi * xi;
        const xi3 = xi2 * xi;
        const xi4 = xi3 * xi;
        const xi2yi = xi2 * yi;
        const xiyi = xi * yi;

        sxi += xi;
        sxi2 += xi2;
        sxiyi += xiyi;
        sxi2yi += xi2yi;
        syi += yi;
        sxi3 += xi3;
        sxi4 += xi4;
    }

    const Sxx = sxi2 - sxi * sxi / count;
    const Sxy = sxiyi - sxi * syi / count;
    const Sxx2 = sxi3 - sxi * sxi2 / count;
    const Sx2y = sxi2yi - sxi2 * syi / count;
    const Sx2x2 = sxi4 - sxi2 * sxi2 / count;

    const numerator = Sxy * Sx2x2 - Sx2y * Sxx2;
    const denominator = Sxx * Sx2x2 - Sxx2 * Sxx2;
    if (denominator == 0) {
        return 0;
    }
    return numerator / denominator;
}

type Movement = {
    x: number
    y: number
    timeStamp: number
}

export class VelocityTracker {
    private movements: Movement[] = [];
    private lastEventTimestamp = 0;
    private readonly historySize: number = 20;
    private readonly assumePointerStoppedTime: number = 40;

    constructor({historySize, assumePointerStoppedTime}: {historySize?: number, assumePointerStoppedTime?: number} = {}) {
        if (historySize != null) {
            this.historySize = historySize;
        }
        if (assumePointerStoppedTime != null) {
            this.assumePointerStoppedTime = assumePointerStoppedTime;
        }
    }

    addMovement(movement: Movement) {
        if (this.movements.length && movement.timeStamp >= this.lastEventTimestamp + this.assumePointerStoppedTime) {
            this.clear();
        }

        this.lastEventTimestamp = movement.timeStamp;
        this.movements.push(movement);

        if (this.movements.length > this.historySize) {
            this.movements.splice(0, 1);
        }
    }

    /**
     *
     * @param id
     * @param units The units you would like the velocity in. A value of 1 provides pixels per millisecond, 1000 provides pixels per second, etc.
     * @param maxVelocity
     * @returns {{vx: number, vy: number}}
     */
    computeCurrentVelocity(units = 1, maxVelocity = Number.MAX_SAFE_INTEGER) {
        const HORIZON = 120;
        const x = [];
        const y = [];
        const time = [];

        const newestMovement = this.movements[this.movements.length - 1];
        for (let i = this.movements.length - 1; i >= 0; --i) {
            const movement = this.movements[i];
            const age = newestMovement.timeStamp - movement.timeStamp;
            if (age > HORIZON) {
                break;
            }

            x.unshift(movement.x);
            y.unshift(movement.y);
            time.unshift(-age / 1000);
        }

        let vx = solveUnweightedLeastSquaresDeg2(time, x, x.length) * units / 1000;
        if (vx > maxVelocity) {
            vx = maxVelocity;
        } else if (vx < -maxVelocity) {
            vx = -maxVelocity;
        }

        let vy = solveUnweightedLeastSquaresDeg2(time, y, y.length) * units / 1000;
        if (vy > maxVelocity) {
            vy = maxVelocity;
        } else if (vy < -maxVelocity) {
            vy = -maxVelocity;
        }
        return { vx, vy, count: time.length };
    }

    clear() {
        this.movements.splice(0);
    }

}